import { useEffect, useMemo } from 'react'
import { Radar } from 'react-chartjs-2'
import 'chart.js/auto'
import { DialogTitle } from '@/components/ui/dialog'
import { useHall } from '@/contexts/HallProvider'
import { SpeakingRole } from '@db8/shared'
import { usePoll } from '@/contexts/PollProvider'
import useDarkMode from '@/hooks/useDarkMode'
import TextLoading from '@/components/ui/textLoading'

export const RadarChartResults = ({ onNext }) => {
  const { hall, isOwner } = useHall()
  const { pollResults, determineWinner, hasEveryoneVoted } = usePoll()
  const isDark = useDarkMode()

  useEffect(() => {
    if (isOwner) determineWinner()
  }, [hall, hasEveryoneVoted])

  const data = useMemo(
    () => ({
      labels: hall?.evaluationCriteria,
      datasets: [
        {
          label: `${hall.affirmingTeamName}`,
          data: hall?.evaluationCriteria.map(
            (criterion) => pollResults[SpeakingRole.Affirmative][criterion],
          ),
          backgroundColor: 'rgba(34, 197, 94, 0.2)',
          borderColor: 'rgba(34, 197, 94, 1)',
          borderWidth: 2,
        },
        {
          label: `${hall.dissentingTeamName}`,
          data: hall?.evaluationCriteria.map(
            (criterion) => pollResults[SpeakingRole.Negative][criterion],
          ),
          backgroundColor: 'rgba(45, 96, 192, 0.2)',
          borderColor: 'rgba(45, 96, 192, 1)',
          borderWidth: 2,
        },
      ],
    }),
    [hall, pollResults],
  )

  const maxVotes = useMemo(
    () =>
      Math.max(
        ...hall?.evaluationCriteria.flatMap((criterion) => [
          pollResults[SpeakingRole.Affirmative][criterion],
          pollResults[SpeakingRole.Negative][criterion],
        ]),
      ),
    [pollResults],
  )

  const options = useMemo(() => {
    const dynamicStepSize = Math.ceil(maxVotes / 5) || 1

    return {
      scales: {
        r: {
          grid: {
            color: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0,0,0, 0.2)',
          },
          angleLines: {
            color: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0,0,0, 0.3)',
          },
          ticks: {
            backdropColor: 'transparent',
            color: isDark ? '#fff' : '#000',
            beginAtZero: true,
            max: dynamicStepSize * 5,
            stepSize: dynamicStepSize,
          },
          pointLabels: {
            color: isDark ? '#fff' : '#000',
          },
        },
      },
      plugins: {
        legend: {
          labels: {
            color: isDark ? '#fff' : '#000',
          },
        },
      },
    }
  }, [pollResults, maxVotes])

  return (
    <div>
      <DialogTitle className="text-xl font-semibold mb-4 text-center">
        Evaluation Results
      </DialogTitle>

      {pollResults.castVotes === 0 ? (
        <div className="flex items-center justify-center text-center text-muted-foreground h-full min-h-[400px] px-6 font-semibold">
          <TextLoading text="Waiting for audience members to cast their votes" />
        </div>
      ) : (
        <Radar data={data} options={options} />
      )}
    </div>
  )
}
